import * as React from 'react';
import { useMemo } from 'react';
import { AnytimeFeedbackResponseError } from '../../../../../../types/AnytimeFeedbackRequestDto';
import { AnytimeFeedbackResponseDto } from '../../../../../../types/FeedbackResponseDto';
import API from '../../../../../api';
import * as R from '../../../../../routes';
import RouteError from '../../../../../routing/RouteError';
import triggerPopupAlert from '../../../../components/PopupAlert/trigger';
import Redirect from '../../../../components/Redirect';
import { ErrorMessage } from '../../../../components/Zen/ErrorMessage';
import { handleApiErrors, withData } from '../../../../hoc/withData';
import { isFeatureEnabled } from '../../../../services/Features';
import { goToUrl } from '../../../../services/Router';
import { interpolateAppUrl, prependAppPath } from '../../../../services/Url';
import { ShareAlert } from '../../../Feedback/components/ShareAlert';
import { LoadingIndicator } from '../../components/Provide/LoadingIndicator';
import { AnytimeFeedbackProvideProps } from './presenter';
const api = API.anytimeFeedbackResponse;

export const individualizedErrors = {
    DEADLINE_PASSED: () => (
        <ErrorMessage
            illustrationSrc={require('../../../Feedback/feedback_deadline_passed.svg')}
            title="Deadline Passed"
            body={
                <p>
                    {isFeatureEnabled('FEATURE_anytimeFeedbackGive')
                        ? 'The time to write your feedback is over.'
                        : 'The time to answer this feedback request is over.'}
                </p>
            }
        />
    ),
};

const errorComponent = handleApiErrors<OuterProps, AnytimeFeedbackResponseError>(individualizedErrors, RouteError);

type OuterProps = {
    requestId: string;
    reviewerId: string;
    prevPath?: string;
};

type AddedProps = {
    response: AnytimeFeedbackResponseDto;
    component: React.ComponentType<AnytimeFeedbackProvideProps>;
};

const DEFAULT_CLOSE_PATH = prependAppPath(R.FEEDBACK_USER) + '?tab=todo';

const AnytimeFeedbackProvideContainer: React.FC<OuterProps & AddedProps> = ({
    response,
    prevPath,
    component: AnytimeFeedbackProvide,
}) => {
    const actions = useMemo<AnytimeFeedbackProvideProps['actions']>(
        () => ({
            updateAnswers: answers => api.answer(response.requestId, response.reviewer.id, answers),
            share: () =>
                api.share(response.requestId, response.reviewer.id).then(res => {
                    triggerPopupAlert(<ShareAlert response={res} />, 'success', { surviveNavigation: true });
                    return res;
                }),
            close: () => goToUrl(prevPath || DEFAULT_CLOSE_PATH),
        }),
        [prevPath, response.requestId, response.reviewer.id]
    );
    return (
        <>
            {(response.status === 'DONE' || response.status === 'SHARED') && (
                <Redirect to={interpolateAppUrl(R.FEEDBACK_USER_VIEW_MULTIPLE, { id: response.requestId })} />
            )}
            <AnytimeFeedbackProvide response={response} actions={actions} />
        </>
    );
};

export default withData<AddedProps, OuterProps>({
    resolve: {
        component: () =>
            import(/* webpackChunkName: 'AnytimeFeedbackProvide' */ './presenter').then(
                ({ AnytimeFeedbackProvide }) => AnytimeFeedbackProvide
            ),
    },
    observe: {
        response: ({ requestId, reviewerId }) => api.get.createObservable(requestId, reviewerId),
    },
    pendingComponent: LoadingIndicator,
    errorComponent,
})(AnytimeFeedbackProvideContainer);
